<template>
  <footer class="dk-footer" :class="footerClass">
    <div class="dk-container dk-footer__grid">
      <!-- Composed: the seller's `footer` section blocks, in their order.
           `page_link` is excluded — it renders in the bottom bar, not the grid,
           so it must not occupy an (empty) column here. -->
      <template v-if="columnBlocks.length">
        <!-- A menu column's root IS the <details>, exactly as in the automatic
             layout below, so one set of accordion rules covers both. -->
        <details
          v-for="b in columnBlocks"
          :key="b.id"
          class="dk-footer__col"
          :class="[`dk-footer__col--${b.type}`, blockLayoutClass(b.settings)]"
          :style="blockLayoutStyle(b.settings)" :data-dk-block="b.id"
          :open="b.type === 'menu' ? expanded : true"
        >
          <summary v-if="b.type === 'menu'" class="dk-footer__coltitle" @click="onSummaryClick">
            <span>{{ b.settings.heading || "Menu" }}</span>
            <svg class="dk-footer__chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </summary>
          <!-- Non-menu blocks are not disclosures: an empty, hidden summary
               keeps <details> valid markup while the content always shows. -->
          <summary v-else class="dk-footer__nosummary" />

          <ul v-if="b.type === 'menu'">
            <li v-for="link in blockMenu(b)" :key="link.url">
              <RouterLink :to="link.url">{{ link.label }}</RouterLink>
            </li>
          </ul>

          <!-- logo_social: the brand column. Never collapses, like the
               automatic layout's brand block. -->
          <template v-else-if="b.type === 'logo_social'">
            <img
              v-if="blockLogo(b)"
              class="dk-footer__logo"
              :src="blockLogo(b)"
              :alt="bootstrap.storeName || 'Store'"
              :style="{ width: `${Number(b.settings.logo_width) || 140}px` }"
            />
            <div v-else class="dk-footer__name">{{ bootstrap.storeName || "Store" }}</div>
            <div v-if="!isBlankRichHtml(b.settings.text)" class="dk-footer__text" v-html="toRichHtml(b.settings.text)" />
            <address v-if="showContact" class="dk-footer__contact">
              <div v-if="addressLine">{{ addressLine }}</div>
              <div v-if="store?.contact_phone">{{ store.contact_phone }}</div>
              <a v-if="store?.contact_email" :href="`mailto:${store.contact_email}`">{{ store.contact_email }}</a>
            </address>
            <div v-if="b.settings.show_social !== false && socials.length" class="dk-footer__social">
              <a v-for="s in socials" :key="s.platform" :href="s.url" target="_blank" rel="noopener" :aria-label="s.platform">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path :d="s.icon" /></svg>
              </a>
            </div>
          </template>

          <template v-else-if="b.type === 'newsletter'">
            <div class="dk-footer__coltitle">{{ b.settings.heading || "Stay in touch" }}</div>
            <div v-if="!isBlankRichHtml(b.settings.text)" class="dk-footer__text" v-html="toRichHtml(b.settings.text)" />
            <form class="dk-footer__news" @submit.prevent="subscribe">
              <input
                v-model="email"
                type="email"
                required
                placeholder="you@email.com"
                aria-label="Email"
                :disabled="busy"
              />
              <!-- Honeypot, same as the newsletter SECTION: off-screen, never
                   focusable, always empty for a human. -->
              <input v-model="website" class="dk-hp" type="text" tabindex="-1" autocomplete="off" aria-hidden="true" />
              <button type="submit" class="dk-btn" :disabled="busy">
                {{ b.settings.button_label || "Subscribe" }}
              </button>
            </form>
            <p v-if="subscribed" class="dk-footer__text">Thanks — you're on the list!</p>
            <p v-else-if="subError" class="dk-footer__text">{{ subError }}</p>
          </template>

          <template v-else-if="b.type === 'custom'">
            <div v-if="b.settings.heading" class="dk-footer__coltitle">{{ b.settings.heading }}</div>
            <!-- v-html, like the page/article/rich-text renderers: the content is
                 the merchant's own, authored in their own dashboard. -->
            <div class="dk-footer__text" v-html="String(b.settings.content ?? '')" />
          </template>

          <template v-else-if="b.type === 'follow_cta'">
            <div class="dk-footer__coltitle">{{ b.settings.heading || "Follow along" }}</div>
            <div v-if="!isBlankRichHtml(b.settings.text)" class="dk-footer__text" v-html="toRichHtml(b.settings.text)" />
            <a
              v-if="b.settings.button_label && linkUrl(b.settings.button_link)"
              class="dk-btn dk-footer__cta"
              :href="linkUrl(b.settings.button_link)"
            >{{ b.settings.button_label }}</a>
          </template>
        </details>
      </template>

      <!-- Automatic: what every store rendered before `layout.footer` was wired,
           and what a store with no footer section still renders. -->
      <template v-else>
        <div class="dk-footer__brand">
          <div class="dk-footer__name">{{ bootstrap.storeName || "Store" }}</div>
          <div v-if="!isBlankRichHtml(footerText)" class="dk-footer__text" v-html="toRichHtml(footerText)" />
          <address v-if="showContact" class="dk-footer__contact">
            <div v-if="addressLine">{{ addressLine }}</div>
            <div v-if="store?.contact_phone">{{ store.contact_phone }}</div>
            <a v-if="store?.contact_email" :href="`mailto:${store.contact_email}`">{{ store.contact_email }}</a>
          </address>
          <div v-if="showSocial && socials.length" class="dk-footer__social">
            <a v-for="s in socials" :key="s.platform" :href="s.url" target="_blank" rel="noopener" :aria-label="s.platform">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path :d="s.icon" /></svg>
            </a>
          </div>
        </div>

        <!-- link columns (menu columns + policies) — 4-up on desktop, collapsible
             accordion (one column) on mobile via native <details>. -->
        <!-- `--menu` because these ARE menu columns: it is what the mobile
             accordion rules key off, shared with the composed path's menu
             blocks so both behave identically. -->
        <details
          v-for="(col, i) in linkColumns"
          :key="col.title + i"
          class="dk-footer__col dk-footer__col--menu"
          :open="expanded"
        >
          <summary class="dk-footer__coltitle" @click="onSummaryClick">
            <span>{{ col.title }}</span>
            <svg class="dk-footer__chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </summary>
          <ul>
            <li v-for="link in col.links" :key="link.url"><RouterLink :to="link.url">{{ link.label }}</RouterLink></li>
          </ul>
        </details>
      </template>
    </div>

    <div class="dk-container dk-footer__bar">
      <span>{{ copyright }}</span>
      <!-- `page_link` blocks: Impulse puts these in the bottom bar, which is also
           the only placement where repeating the block means something. -->
      <RouterLink v-for="(l, i) in barLinks" :key="i" :to="l.url" class="dk-footer__barlink">{{ l.label }}</RouterLink>
    </div>
  </footer>
</template>

<script setup lang="ts">
import type { BlockInstance } from "@dk/theme"
// The four footer texts are rich text now. No `dk-rte` on their hosts, unlike
// every other v-html surface: footer text is chrome — tighter metrics and its
// own link colour — which is why this file is exempt from the contract test.
import { blockLayoutClass, blockLayoutStyle, isBlankRichHtml, toRichHtml } from "@dk/theme"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { defaultCopyright, footerBarLinks, footerColumnBlocks, footerSection, footerSetting } from "../lib/footer"
import { subscribeNewsletter } from "../lib/newsletter"
import { imgSrc, linkUrl } from "../sections/helpers"
import { useBootstrapStore } from "../stores/bootstrap"

const bootstrap = useBootstrapStore()
const year = new Date().getFullYear()

const store = computed(() => bootstrap.store)

// ---- the `footer` layout section -------------------------------------------
// `Theme.layout.footer` was declared, initialised to [] and read by nothing;
// this component rendered from the `ThemeSettings.footer` group instead. Same
// resolve order the header uses: the section's value when SET, else that group.
// A store with no footer section — every store authored before this — renders
// byte-identically.
const section = computed(() => footerSection(bootstrap.themeDoc?.layout?.footer))
const group = computed<Record<string, unknown>>(() => (bootstrap.themeSettings.footer ?? {}) as Record<string, unknown>)
const fs = (key: string, groupKey?: string) =>
  footerSetting(section.value?.settings, group.value, key, groupKey)

const columnBlocks = computed<BlockInstance[]>(() => footerColumnBlocks(section.value))
const barLinks = computed(() => footerBarLinks(section.value))

const footerText = computed(() => String(fs("text") ?? ""))
const showContact = computed(() => fs("show_contact") !== false)
const showSocial = computed(() => fs("show_social") !== false)
const showPolicies = computed(() => fs("show_policies") !== false)
const copyright = computed(() => String(fs("copyright") ?? "") || defaultCopyright(year, bootstrap.storeName || "Store"))

// Layout / alignment / width config.
const footerClass = computed(() => {
  const centered = fs("layout") === "centered"
  return {
    "dk-footer--stacked": centered,
    "dk-footer--center": centered || fs("alignment") === "center",
    "dk-footer--narrow": fs("content_width", "width") === "narrow",
    // The mobile accordion applies to the multi-column layout only; the
    // centered stack stays expanded (a single centred column at every width).
    "dk-footer--accordion": !centered,
  }
})

// ---- block helpers ---------------------------------------------------------
const blockLogo = (b: BlockInstance) => imgSrc(b.settings.logo) || imgSrc(bootstrap.themeSettings.logo?.src)
const blockMenu = (b: BlockInstance) => bootstrap.menuByHandle(String(b.settings.menu || "footer"))

// The newsletter block posts to the same endpoint as the newsletter SECTION, so
// a footer signup lands in the same `store_form_submissions` list.
const email = ref("")
const website = ref("")
const busy = ref(false)
const subscribed = ref(false)
const subError = ref("")
async function subscribe() {
  busy.value = true
  subError.value = ""
  try {
    await subscribeNewsletter(email.value, website.value)
    subscribed.value = true
    email.value = ""
  } catch {
    subError.value = "Sorry — that didn't go through. Please try again."
  } finally {
    busy.value = false
  }
}

// Mobile accordion: below 800px (the header's breakpoint) the link columns
// collapse to one column with a per-section disclosure; on desktop — and for
// the centered stack at any width — they stay expanded.
const collapsible = computed(() => fs("layout") !== "centered")
let mql: MediaQueryList | null = null
const isDesktop = ref(true)
if (typeof window !== "undefined") {
  mql = window.matchMedia("(min-width: 800px)")
  isDesktop.value = mql.matches // avoid an expanded→collapsed flash on mobile
}
const expanded = computed(() => !collapsible.value || isDesktop.value)
const applyViewport = () => { isDesktop.value = mql ? mql.matches : true }
onMounted(() => mql?.addEventListener("change", applyViewport))
onBeforeUnmount(() => mql?.removeEventListener("change", applyViewport))

// Only toggle the accordion when it's actually collapsible (mobile, columns
// layout); on desktop the disclosure is inert so a click can't hide a column.
function onSummaryClick(e: MouseEvent) {
  if (expanded.value) e.preventDefault()
}

const addressLine = computed(() => {
  const a = store.value?.address
  if (!a) return ""
  return [a.line1, a.line2, a.city, a.province, a.zip, a.country].filter(Boolean).join(", ")
})

const policyLinks = computed(() => {
  const p = (bootstrap.storeSettings.policies ?? {}) as Record<string, { title?: string }>
  return Object.entries(p).map(([key, v]) => ({ key, title: v?.title || key }))
})

// Footer link columns: top-level menu items with children become columns;
// childless items collect into a "Shop" column; the store policies form the
// trailing "Policies" column. Rendered uniformly so the accordion covers all.
const linkColumns = computed(() => {
  const items = bootstrap.footerMenu
  const cols: { title: string; links: { label: string; url: string }[] }[] = []
  const flat: { label: string; url: string }[] = []
  for (const it of items) {
    if (it.children?.length) cols.push({ title: it.label, links: it.children })
    else flat.push({ label: it.label, url: it.url })
  }
  if (flat.length) cols.unshift({ title: "Shop", links: flat })
  if (showPolicies.value && policyLinks.value.length) {
    cols.push({
      title: "Policies",
      links: policyLinks.value.map((p) => ({ label: p.title, url: `/policies/${p.key}` })),
    })
  }
  return cols
})

// Compact brand glyphs (single-path, 24×24) — 0 dependencies.
const ICONS: Record<string, string> = {
  facebook: "M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07",
  instagram: "M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.2-4.35-2.62-6.78-6.98-6.98C15.67.01 15.26 0 12 0m0 5.84A6.16 6.16 0 1018.16 12 6.16 6.16 0 0012 5.84M12 16a4 4 0 114-4 4 4 0 01-4 4m6.41-10.85a1.44 1.44 0 101.44 1.44 1.44 1.44 0 00-1.44-1.44",
  tiktok: "M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.08-.14 1.62.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07",
  x: "M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93zM17.61 20.64h2.04L6.49 3.24H4.31z",
  youtube: "M23.5 6.2a3.02 3.02 0 00-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 00.5 6.2 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.8 3.02 3.02 0 002.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 002.12-2.14A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.8M9.55 15.57V8.43L15.82 12z",
  linkedin: "M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12M7.12 20.45H3.56V9h3.56zM22.22 0H1.77A1.75 1.75 0 000 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0",
  pinterest: "M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.42 7.63 11.17-.11-.95-.2-2.4.04-3.44.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.02 0 1.51.77 1.51 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.17 1.79 2.17 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.92-5.05-4.92-3.44 0-5.46 2.58-5.46 5.25 0 1.04.4 2.15.9 2.76.1.12.11.22.08.34l-.34 1.36c-.05.22-.18.27-.41.16-1.5-.7-2.44-2.9-2.44-4.67 0-3.8 2.76-7.29 7.96-7.29 4.18 0 7.43 2.98 7.43 6.96 0 4.15-2.62 7.49-6.25 7.49-1.22 0-2.37-.63-2.76-1.38l-.75 2.86c-.27 1.04-1 2.35-1.49 3.15C9.57 23.83 10.77 24 12 24c6.63 0 12-5.37 12-12S18.63 0 12 0",
}
const socials = computed(() => {
  const s = (bootstrap.themeSettings.social ?? {}) as Record<string, string>
  return Object.entries(s)
    .filter(([p, url]) => url && ICONS[p])
    .map(([platform, url]) => ({ platform, url, icon: ICONS[platform]! }))
})
</script>

<style scoped>
.dk-footer {
  margin-top: 4rem;
  background: var(--dk-color-footer-bg, #111);
  color: var(--dk-color-footer-fg, #eee);
}
.dk-footer__grid {
  display: grid;
  /* Desktop: four-up — a wider brand column + three link columns, matching the
     seeded footer menu (brand · Shop · Company · Policies). Extra columns wrap
     to the next row; fewer just leave the trailing track empty. NB: an integer
     repeat() is used (not auto-fit) — mixing a flexible `fr` track with
     auto-fit is invalid CSS and gets dropped by the build's strict parser. */
  grid-template-columns: 1.6fr repeat(3, minmax(0, 1fr));
  gap: 2rem;
  /* padding-block only — this element also carries .dk-container, whose horizontal
     padding must survive (the `padding` shorthand would zero it, pushing content
     flush to the screen edge on mobile). */
  padding-block: 3rem 2rem;
}
/* A composed footer's first column is the brand column, same as the automatic
   layout's `.dk-footer__brand`; the rest are equal link tracks. */
.dk-footer__col--logo_social {
  grid-column: span 1;
}
.dk-footer__name {
  font-weight: 600;
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
}
.dk-footer__logo {
  display: block;
  max-width: 100%;
  height: auto;
  margin-bottom: 0.75rem;
}
.dk-footer__text {
  font-size: 0.85rem;
  margin: 0 0 0.75rem;
  max-width: 32ch;
}
.dk-footer__contact {
  font-style: normal;
  font-size: 0.85rem;
  line-height: 1.6;
}
.dk-footer__contact a {
  color: inherit;
}
.dk-footer__social {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
}
.dk-footer__social a {
  color: inherit;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.dk-footer__social a:hover {
  opacity: 1;
}
.dk-footer__coltitle {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.75rem;
  list-style: none; /* drop the native <summary> disclosure marker */
}
.dk-footer__coltitle::-webkit-details-marker {
  display: none;
}
/* A non-menu block still needs a <summary> for <details> to be valid markup,
   but it is not a disclosure — hide it and keep the content always open. */
.dk-footer__nosummary {
  display: none;
}
.dk-footer__chev {
  display: none; /* desktop: sections are always open, no disclosure */
  flex: none;
  transition: transform 0.2s ease;
}
.dk-footer__col ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
/* Footer text is the full footer-fg, which theme-templates' contrast test holds to
   4.5:1 on footer-bg. It used to fade to 60–90%, and on a coloured footer no fade
   clears 4.5:1: Shrine's cream on its own red was 4.24 at full strength and 2.7 at 70%. */
.dk-footer__col a {
  color: inherit;
  font-size: 0.88rem;
  text-decoration: none;
}
.dk-footer__col a:hover {
  text-decoration: underline;
}
.dk-footer__news {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.dk-footer__news input[type="email"] {
  flex: 1 1 8rem;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
  border-radius: var(--dk-radius, 4px);
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.88rem;
}
.dk-footer__cta {
  display: inline-block;
  margin-top: 0.25rem;
}
.dk-footer__bar {
  border-top: 1px solid color-mix(in srgb, currentColor 14%, transparent);
  padding-block: 1.25rem; /* keep .dk-container's horizontal padding (see grid) */
  font-size: 0.8rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
}
.dk-footer__barlink {
  color: inherit;
  text-decoration: none;
}
.dk-footer__barlink:hover {
  text-decoration: underline;
}

/* ---- configurable layout / alignment / width ---- */

/* Narrow: box the content and centre the container. */
.dk-footer--narrow .dk-footer__grid,
.dk-footer--narrow .dk-footer__bar {
  max-width: 760px;
}

/* Centre alignment: text + inline blocks centred within their column. */
.dk-footer--center .dk-footer__grid {
  text-align: center;
}
.dk-footer--center .dk-footer__text {
  margin-left: auto;
  margin-right: auto;
}
.dk-footer--center .dk-footer__contact,
.dk-footer--center .dk-footer__social,
.dk-footer--center .dk-footer__news {
  justify-content: center;
}
.dk-footer--center .dk-footer__col ul {
  align-items: center;
}
.dk-footer--center .dk-footer__bar {
  text-align: center;
  justify-content: center;
}

/* Centered stack: a single centred column instead of the multi-column grid. */
.dk-footer--stacked .dk-footer__grid {
  grid-template-columns: 1fr;
  justify-items: center;
  gap: 1.5rem;
}

/* ---- mobile: one column, link sections collapse into an accordion ---- */
@media (max-width: 799.98px) {
  .dk-footer__grid {
    grid-template-columns: 1fr;
    gap: 0;
    padding-block: 2rem 0.5rem; /* keep .dk-container's horizontal padding */
  }
  /* The brand column heads the stack and is not a divided section — true of
     `.dk-footer__brand` (automatic) and `logo_social` (composed) alike. */
  .dk-footer--accordion .dk-footer__brand,
  .dk-footer--accordion .dk-footer__col--logo_social {
    padding-bottom: 1.25rem;
  }
  .dk-footer--accordion .dk-footer__col:not(.dk-footer__col--logo_social) {
    border-top: 1px solid color-mix(in srgb, currentColor 14%, transparent);
  }
  /* A menu column's padding comes from its tappable summary row (below); every
     other column has no disclosure, so it carries its own. */
  .dk-footer--accordion .dk-footer__col:not(.dk-footer__col--logo_social):not(.dk-footer__col--menu) {
    padding-block: 1rem;
  }
  .dk-footer--accordion .dk-footer__col--menu .dk-footer__coltitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin: 0;
    padding: 1rem 0;
    cursor: pointer;
  }
  .dk-footer--accordion .dk-footer__chev {
    display: block;
  }
  .dk-footer--accordion details[open] .dk-footer__chev {
    transform: rotate(180deg);
  }
  .dk-footer--accordion details > ul {
    padding-bottom: 1rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dk-footer__chev {
    transition: none;
  }
}
</style>
